// Configuration for the Agricultural LULC interactive demo

import type {
  LulcClass,
  LulcChangeClass,
  CropClass,
  CropRasterClass,
  CropHarvestClass,
  ForestClass,
  WizardStepId,
} from "../types/agricultural-lulc";

// --- Classification Colours ---

export const LULC_COLORS: Record<LulcClass, string> = {
  "Crop Land": "#fdf06f",
  "Range Land": "#3dbf35",
  Forest: "#018933",
  "Built Up": "#e75a5a",
  Waterbody: "#003fbf",
  "Barren Land": "#fdae61",
  "Pasture/Fallow": "#6ee16e",
  "Plantation/Trees": "#01896e",
};

export const CROP_COLORS: Record<CropClass, string> = {
  Wheat: "#FFD700",
  Canola: "#FFBF00",
  Barley: "#E6BE8A",
};

export const CROP_RASTER_COLORS: Record<CropRasterClass, string> = {
  Sorghum: "#E1C16E",
  Cotton: "#F5F5F5",
  "Other Crop": "#ffff99",
  Fallow: "#f4a460",
};

// Harvest palette: each cropped class splits into harvested (saturated) vs
// standing (washed out). Other Crop and Fallow have no harvest signal.
export const CROP_HARVEST_COLORS: Record<CropHarvestClass, string> = {
  "Sorghum (harvested)": "#8B6914",
  "Sorghum (standing)": "#E1C16E",
  "Cotton (harvested)": "#7F8C8D",
  "Cotton (standing)": "#F5F5F5",
  "Other Crop": "#ffff99",
  Fallow: "#f4a460",
};

export const LULC_CHANGE_COLORS: Record<LulcChangeClass, string> = {
  "No Change": "#A0A0A0",
  "Gained Cropland": "#fdf06f", // matches LULC_COLORS["Crop Land"]
  Reforestation: "#018933", // matches LULC_COLORS["Forest"]
  "Lost Cropland": "#e75a5a", // warning red (also LULC_COLORS["Built Up"])
  Deforestation: "#8B4513", // brown — degraded, distinct from Lost Cropland
  "Other Change": "#6ee16e", // matches LULC_COLORS["Pasture/Fallow"]
};

// Keys above must match the `dominant_class` values in the source GeoJSON,
// so they can't be renamed. This map provides neutral display labels for the
// legend — paired "Gain" / "Loss" terminology avoids the politically-charged
// "Reforestation" / "Deforestation" framing.
export const LULC_CHANGE_LABELS: Record<LulcChangeClass, string> = {
  "No Change": "No Change",
  "Gained Cropland": "Cropland Gain",
  "Lost Cropland": "Cropland Loss",
  Reforestation: "Tree Cover Gain",
  Deforestation: "Tree Cover Loss",
  "Other Change": "Other Change",
};

export const FOREST_COLORS: Record<ForestClass, string> = {
  Woodland: "#228B22",
  Open: "#32CD32",
  Closed: "#006400",
  Unclassified: "#B7E4C7",
};

// --- Wizard Step Definitions ---

export interface StepDefinition {
  id: WizardStepId;
  title: string;
  description: string;
  showYearToggle: boolean;
  showLegend: boolean;
}

export const WIZARD_STEPS: StepDefinition[] = [
  {
    id: "challenge",
    title: "The Challenge",
    description:
      "Understanding agricultural land use at scale is difficult. Traditional methods rely on manual surveys and outdated records, making it impossible to assess crop coverage, land productivity, and environmental change across large areas in near-real-time.",
    showYearToggle: false,
    showLegend: false,
  },
  {
    id: "satellite",
    title: "Satellite Imagery",
    description:
      "Geoscape's agricultural data products are derived from 10-metre resolution satellite imagery collected across Australia. This high-resolution imagery is the foundation for the classification algorithms that identify land use patterns, crop types, and vegetation cover. Use the toggle to switch between 2024 and 2025 captures.",
    showYearToggle: true,
    showLegend: false,
  },
  {
    id: "lulc",
    title: "Land Use & Land Cover",
    description:
      "Land Use Land Cover (LULC) classification identifies how every parcel of land is being used — from cropping and grazing to urban and natural vegetation. Toggle between years to see how land use shifts season to season.",
    showYearToggle: true,
    showLegend: true,
  },
  {
    id: "crops",
    title: "Crops & Harvest",
    description:
      "Pixel-level crop classification (summer 2024) distinguishes sorghum, cotton, other cropping, and fallow. Harvest detection further separates harvested vs still-standing pixels — supporting yield forecasting, agricultural finance, and land-productivity analysis.",
    showYearToggle: false,
    showLegend: true,
  },
  {
    id: "forest",
    title: "Forest Coverage",
    description:
      "Forest classification maps tree coverage density and type across rural regions. Woodland, open forest, and sparse vegetation are distinguished — supporting environmental monitoring, carbon accounting, bushfire risk assessment, and land management planning.",
    showYearToggle: false,
    showLegend: true,
  },
  {
    id: "parcel",
    title: "Property-Level Insight",
    description:
      "Every classification is attributed to individual cadastral parcels from the National Cadastre. This enables property-level analysis — land valuation, agricultural finance, environmental compliance, and development planning. Each parcel carries detailed coverage statistics and classification breakdowns.",
    showYearToggle: false,
    showLegend: true,
  },
  {
    id: "comparison",
    title: "Temporal Comparison",
    description:
      "Compare year-over-year changes to identify crop rotations, land conversions, and environmental shifts. The temporal comparison highlights parcels that have changed classification — from cropland to fallow, forest clearance, or new development. Track change at scale across entire regions.",
    showYearToggle: false,
    showLegend: true,
  },
  {
    id: "explore",
    title: "Explore Freely",
    description:
      "You've completed the guided tour. The map is now yours to explore — click any parcel to view its full attribution, toggle layers on and off, and compare years.",
    showYearToggle: false,
    showLegend: false,
  },
];

// --- Camera Positions ---

const CARRATHOOL_CENTER: [number, number] = [145.4253, -33.6256];

export const CAMERA_POSITIONS: Record<
  WizardStepId,
  { center: [number, number]; zoom: number; minZoom: number; maxZoom: number }
> = {
  challenge: { center: CARRATHOOL_CENTER, zoom: 8, minZoom: 8, maxZoom: 8 },
  satellite: { center: CARRATHOOL_CENTER, zoom: 9, minZoom: 8, maxZoom: 13 },
  lulc: { center: [145.5713, -33.9059], zoom: 11, minZoom: 10, maxZoom: 13 },
  crops: { center: [145.481, -33.5231], zoom: 11, minZoom: 10, maxZoom: 13 },
  forest: { center: [145.4863, -33.0847], zoom: 11, minZoom: 10, maxZoom: 13 },
  parcel: { center: [145.37, -33.7], zoom: 14, minZoom: 13, maxZoom: 16 },
  comparison: {
    center: [146.2913, -33.8433],
    zoom: 12,
    minZoom: 12,
    maxZoom: 12,
  },
  explore: { center: CARRATHOOL_CENTER, zoom: 9, minZoom: 8, maxZoom: 16 },
};

export const EXPLORE_PAN_BOUNDS: [[number, number], [number, number]] = [
  [144.2, -34.75],
  [146.65, -32.5],
];

// --- Pre-selected Parcel ---

export const FEATURED_PARCEL_ID = "cpbb631cff1e21e";

// PMTiles archives — produced by scripts/demo-raster-lulc/tile-vector-lulc.sh.
// File stem matches the source GeoJSON stem (and the tippecanoe `-l` layer
// name), so the maplibre `source-layer` is the URL basename minus `.pmtiles`
// (e.g. `carrathool_lulc_2024`).
export const PMTILES_SOURCES = {
  lgaBoundary: "/demos/static/pmtiles/carrathool.pmtiles",
  parcels: "/demos/static/pmtiles/carrathool_parcels_full.pmtiles",
  lulc2024: "/demos/static/pmtiles/carrathool_lulc_2024.pmtiles",
  lulc2025: "/demos/static/pmtiles/carrathool_lulc_2025.pmtiles",
  crops2025: "/demos/static/pmtiles/carrathool_crops_winter2025.pmtiles",
  forest2025: "/demos/static/pmtiles/carrathool_forest_2024.pmtiles",
  lulcChange: "/demos/static/pmtiles/carrathool_lulc_change.pmtiles",
  lulcChangePatches: "/demos/static/pmtiles/carrathool_lulc_change_patches.pmtiles",
};

// Per-PMTiles `source-layer` name (tippecanoe `-l` value) — the file stem.
// Maplibre needs this to find the layer inside the .pmtiles archive.
export const PMTILES_SOURCE_LAYERS = {
  lgaBoundary: "carrathool",
  parcels: "carrathool_parcels_full",
  lulc2024: "carrathool_lulc_2024",
  lulc2025: "carrathool_lulc_2025",
  crops2025: "carrathool_crops_winter2025",
  forest2025: "carrathool_forest_2024",
  lulcChange: "carrathool_lulc_change",
  lulcChangePatches: "carrathool_lulc_change_patches",
};

export const LULC_RASTER_TILES: Record<"2024" | "2025" | "change", string> = {
  "2024": "/demos/static/raster/lulc-2024/{z}/{x}/{y}.png",
  "2025": "/demos/static/raster/lulc-2025/{z}/{x}/{y}.png",
  change: "/demos/static/raster/lulc-change/{z}/{x}/{y}.png",
};

export const LULC_RASTER_MIN_ZOOM = 8;
export const LULC_RASTER_MAX_ZOOM = 13;

export type SentinelVariant = "default" | "hires";

export const SENTINEL_RASTER_TILES: Record<
  SentinelVariant,
  Record<"2024" | "2025", string>
> = {
  default: {
    "2024": "/demos/static/raster/sentinel-2024/{z}/{x}/{y}.webp",
    "2025": "/demos/static/raster/sentinel-2025/{z}/{x}/{y}.webp",
  },
  hires: {
    "2024": "/demos/static/raster/sentinel-hires-2024/{z}/{x}/{y}.webp",
    "2025": "/demos/static/raster/sentinel-hires-2025/{z}/{x}/{y}.webp",
  },
};

export const SENTINEL_RASTER_MIN_ZOOM = 8;
export const SENTINEL_VARIANT_MAX_ZOOM: Record<SentinelVariant, number> = {
  default: 13,
  hires: 15,
};

export const CROPS_RASTER_TILES =
  "/demos/static/raster/crops-summer2024/{z}/{x}/{y}.png";
export const CROPS_RASTER_MIN_ZOOM = 8;
export const CROPS_RASTER_MAX_ZOOM = 13;

export const HARVEST_RASTER_TILES =
  "/demos/static/raster/harvest-summer2024/{z}/{x}/{y}.png";
export const HARVEST_RASTER_MIN_ZOOM = 8;
export const HARVEST_RASTER_MAX_ZOOM = 13;

export const FOREST_RASTER_TILES =
  "/demos/static/raster/forest-2024/{z}/{x}/{y}.png";
export const FOREST_RASTER_MIN_ZOOM = 8;
export const FOREST_RASTER_MAX_ZOOM = 13;
